import { useEffect, useRef } from "react";
import { useGooglePlaces } from "../hooks/use-google-places";
import ClientUtils from "../utils/ClientUtils";
import CheckmarkIcon from "./icons/AppCheckmarkIcon";
import AppErrorIcon from "./icons/AppErrorIcon";

type TGoogleAdressInputResponse = {
  formattedAddress: string;
  latitude: number;
  longitude: number;
};

const autocompleteCache = new WeakMap<
  HTMLInputElement,
  google.maps.places.Autocomplete
>();

interface AddressInputProps extends React.HTMLProps<HTMLInputElement> {
  onAddressSelected?: (address: TGoogleAdressInputResponse) => void;
  className?: string;
  defaultValue?: string;
}

const GoogleAddressInput = (props: AddressInputProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const places = useGooglePlaces();
  const hasValue = !!props.defaultValue;

  const { className, onAddressSelected, ...rest } = props;

  const onAddressSelectedRef = useRef(onAddressSelected);
  onAddressSelectedRef.current = onAddressSelected;

  useEffect(() => {
    const curr = inputRef.current;
    if (!places || !window.google || !curr) {
      return;
    }

    const existing = autocompleteCache.get(curr);

    if (existing) {
      google.maps.event.clearInstanceListeners(existing);
    }

    const autocomplete = existing ?? new window.google.maps.places.Autocomplete(curr);
    autocompleteCache.set(curr, autocomplete);

    autocomplete.addListener("place_changed", () => {
      const place = autocomplete.getPlace();

      if (place.geometry) {
        const formattedAddress = place.formatted_address ?? "";
        const latitude = place.geometry.location.lat();
        const longitude = place.geometry.location.lng();

        return onAddressSelectedRef.current?.({
          formattedAddress,
          latitude,
          longitude,
        });
      }

      /* 
        If place.geometry doesn't exist, the user clicked enter without first selecting an item.
        In this case, we try to select the first item by firing events on the input.

        While there are less hacky ways of doing this, like creating a custom input widget and
        programatically fetching the results, this is the most pragmatic way and allows us to keep
        all the logic already in place from the google autocomplete widget.
      */

      const firstAutocompleteItem = document.querySelector(".pac-item");
      const curr = inputRef.current;

      if (!firstAutocompleteItem || !curr) {
        return;
      }

      const arrowDownEvent = new KeyboardEvent("keydown", {
        key: "ArrowDown",
        keyCode: 40, // Legacy
        code: "ArrowDown",
        which: 40, // Legacy
        bubbles: true,
        cancelable: true,
      });

      curr.dispatchEvent(arrowDownEvent);

      const enterEvent = new KeyboardEvent("keydown", {
        key: "Enter",
        keyCode: 13, // Legacy
        code: "Enter",
        which: 13, // Legacy
        bubbles: true,
        cancelable: true,
      });

      curr.dispatchEvent(enterEvent);
    });

    return () => {
      google.maps.event.clearInstanceListeners(autocomplete);
    };
  }, [places]);

  useEffect(() => {
    const curr = inputRef.current;
    if (!curr) {
      return;
    }
    const eventListener = (e: KeyboardEvent) => {
      if (e.key === "Enter") {
        /* 
          Preventing default will not prevent the place_changed event to be fired. We prevent default to 
          prevent the form being submitted.
        */
        e.preventDefault();
      }
    };

    curr.addEventListener("keydown", eventListener);

    return () => {
      curr.removeEventListener("keydown", eventListener);
    };
  });

  return (
    <span
      className={ClientUtils.twClassNames(
        props.className,
        "grid grid-cols-[minmax(0,1fr),auto]"
      )}
    >
      <input
        {...rest}
        /* 
          Override the ref intentionally        
        */
        ref={inputRef}
        className="w-full rounded px-4 py-2"
        type="text"
        placeholder="Skriv in address"
        defaultValue={props.defaultValue}
      />
      <span className="flex items-center justify-center px-2">
        {hasValue && <CheckmarkIcon size={24} className="text-green-600" />}
        {!hasValue && <AppErrorIcon size={24} className="text-red-600" />}
      </span>
    </span>
  );
};

export default GoogleAddressInput;